import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import enTranslations from '../locales/en.json';
import arTranslations from '../locales/ar.json';
import legalEnTranslations from '../locales/legal-en.json';
import legalArTranslations from '../locales/legal-ar.json';

type Language = 'en' | 'ar';
type Translations = typeof enTranslations;

const legalTranslations = {
  en: legalEnTranslations,
  ar: legalArTranslations,
} as const;

type LegalTranslations = (typeof legalTranslations)[Language];

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  legal: LegalTranslations;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const translations: Record<Language, Translations> = {
  en: enTranslations,
  ar: arTranslations,
};

const ARABIC_DEFAULT_ROUTES = new Set(['/germany-steps', '/italy-steps', '/lithuania-steps', '/portugal-steps', '/scholarship', '/scholarship/details']);

export function I18nProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [language, setLanguageState] = useState<Language>('ar');

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Update document direction and lang attribute
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  useEffect(() => {
    // Load language from localStorage, route defaults, or site default (Arabic)
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang === 'en' || savedLang === 'ar') {
      setLanguage(savedLang);
      return;
    }

    if (ARABIC_DEFAULT_ROUTES.has(location.pathname)) {
      setLanguage('ar');
      return;
    }

    // No saved preference and no override route -> default to Arabic
    setLanguage('ar');
  }, [location.pathname]);

  const value: I18nContextType = {
    language,
    setLanguage,
    t: translations[language],
    legal: legalTranslations[language],
    isRTL: language === 'ar',
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
